import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { FileText, Loader2, MoreHorizontal, Pencil, RefreshCw } from 'lucide-react';
import { itemToOptions } from '../../utils/GlobalFunctions';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import Select from 'src/components/ui/BrandSelect';
import { adminToolbarSelect } from '../../utils/ReactSelectStyles';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Actions as AGREEMENTS } from '../../../store/actions/agreementsActions';
import { Actions as ADMINACTIONS } from '../../../store/actions/adminActions';
import { isEditableAgreementStatus } from './agreementFormUtils';

const AgreementsList = ({
  getMyAgreements,
  getAllItemsFromDB,
  agreements,
  listLoading,
  listError,
  adminOrgs,
}) => {
  const [selectedOrg, setSelectedOrg] = useState('');
  const [contractsDb, setContractsDb] = useState([]);
  const history = useHistory();
  const location = useLocation();
  const flowOff = agreements?.lastMeta?.flowDisabled;

  useEffect(() => {
    const orgFromQuery = new URLSearchParams(location.search).get('org');
    if (orgFromQuery && orgFromQuery !== selectedOrg) {
      setSelectedOrg(String(orgFromQuery));
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      const res = await getAllItemsFromDB('contracts');
      const data = res?.data;
      const items = data?.items?.message != null ? data?.items?.message : data?.items;
      if (Array.isArray(items)) setContractsDb(items);
    })();
  }, [getAllItemsFromDB]);

  useEffect(() => {
    if (selectedOrg) {
      getMyAgreements(selectedOrg);
    }
  }, [selectedOrg, getMyAgreements]);

  const fromApi = Array.isArray(agreements?.list) ? agreements.list : [];
  // Only fall back to legacy admin "contracts" table when the new API is off (404).
  // If the API returns 200 with { agreements: [] }, show an empty table — not unrelated legacy rows.
  const showRows =
    flowOff && fromApi.length === 0
      ? contractsDb
      : fromApi;
  const orgOptions = (adminOrgs || []).map((o) => ({ value: o.value, label: o.label }));
  const orgValue = orgOptions.find((o) => o.value === selectedOrg) || null;

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <FileText size={20} strokeWidth={1.5} />
            Agreements
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[260px] max-w-md flex-1">
              <Select
                classNamePrefix="cyf-select"
                options={orgOptions}
                isClearable
                placeholder="Select organisation to load API list"
                value={orgValue}
                onChange={(opt) => setSelectedOrg(opt && opt.value != null ? String(opt.value) : '')}
                styles={adminToolbarSelect}
                menuPosition="fixed"
                menuPlacement="auto"
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => selectedOrg && getMyAgreements(selectedOrg)}
            >
              <RefreshCw size={16} strokeWidth={1.5} className="mr-1" />
              Refresh
            </Button>
            <Button type="button" variant="outline" onClick={() => history.push('/admin/sales/catalog')}>
              Catalog pricing
            </Button>
            <Button type="button" onClick={() => history.push('/admin/agreements/new')}>
              New agreement
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {flowOff && contractsDb.length > 0 && (
            <p className="text-sm text-warning mb-3">
              The new agreements API returned 404 (flow not enabled for this build). Showing the legacy admin contracts list below if available.
            </p>
          )}
          {listError && <p className="text-destructive text-sm mb-2">{listError}</p>}
          {listLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          )}
          {!listLoading && (
            <div className="border border-border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Term</TableHead>
                    <TableHead>Pay mode</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(!showRows || showRows.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-muted-foreground text-center py-6">
                        No agreements found. Create one or select an organisation and refresh.
                      </TableCell>
                    </TableRow>
                  )}
                  {showRows.map((row) => {
                    const id = row.id;
                    const name = row.customerOrganisationName || row.customer?.name || '—';
                    return (
                      <TableRow key={id}>
                        <TableCell>{row.type || '—'}</TableCell>
                        <TableCell className="font-medium">{name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {row.status}
                            {row.isLegacyPandaDoc && <Badge className="text-xs">Legacy</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {row.term?.commitmentEndDate
                            ? `Until ${format(parseISO(row.term.commitmentEndDate), 'PPP')}`
                            : row.term?.type || '—'}
                        </TableCell>
                        <TableCell>{row.payMode || '—'}</TableCell>
                        <TableCell>
                          {id && (
                            <div className="flex items-center gap-0.5">
                              <Button variant="ghost" size="sm" asChild>
                                <Link
                                  to={{
                                    pathname: '/admin/agreements/' + encodeURIComponent(id),
                                    search: selectedOrg
                                      ? '?org=' + encodeURIComponent(selectedOrg)
                                      : '',
                                  }}
                                  title="View"
                                >
                                  <MoreHorizontal size={16} strokeWidth={1.5} />
                                </Link>
                              </Button>
                              {selectedOrg && isEditableAgreementStatus(row.status) && (
                                <Button variant="ghost" size="sm" asChild>
                                  <Link
                                    to={{
                                      pathname:
                                        '/admin/agreements/' + encodeURIComponent(id) + '/edit',
                                      search: '?org=' + encodeURIComponent(selectedOrg),
                                    }}
                                    title="Edit"
                                  >
                                    <Pencil size={16} strokeWidth={1.5} />
                                  </Link>
                                </Button>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const mapState = (s) => ({
  agreements: s.agreements,
  listLoading: s.agreements?.loading?.list,
  listError: s.agreements?.error,
  adminOrgs: s.admin?.organisations?.map(itemToOptions) || [],
});

const mapDispatch = (d) => ({
  getMyAgreements: (oid) => d(AGREEMENTS.getMyAgreements(oid)),
  getAllItemsFromDB: (t) => d(ADMINACTIONS.getAllItemsFromDB(t)),
});

export default connect(mapState, mapDispatch)(AgreementsList);
